import { useState } from 'react'
import { repository } from '../../data'
import type { View } from './derive'
import { initials } from './derive'
import { Avatar, useWrite } from './parts'

type Job = 'engineer' | 'technician' | 'intake'

/**
 * People signed in to the network who have never signed in to Kronos. Adding
 * one makes their Kronos account now — they claim it at their first Google
 * sign-in — so they can be put in a team straight away. The job is asked
 * rather than assumed: in Kronos a role is a job, and "technician" is paid
 * by the hour.
 */
export function NotInKronos({ view }: { view: View }): JSX.Element | null {
  const [job, setJob] = useState<Record<string, Job>>({})
  const add = useWrite(
    (email: string, name: string, role: Job) => repository.addPersonFromNetwork({ email, name, role }),
    (_email, name) => `${name} is in Kronos now — put them in a team`,
  )
  if (view.networkOnly.length === 0) return null
  return (
    <section className="cx-panel cx-panel--padded cx-found" aria-label="On the network, not in Kronos">
      <div className="stack" style={{ gap: 2 }}>
        <h2 className="cx-panel__title">On the network, not in Kronos · {view.networkOnly.length}</h2>
        <span className="muted cx-small">They signed in to the network but never to Kronos, so they cannot be given access yet.</span>
      </div>
      <ul className="cx-list">
        {view.networkOnly.map((u) => (
          <li key={u.email} className="cx-list__row cx-found__row">
            <span className="row" style={{ gap: 10, minWidth: 0 }}>
              <Avatar initials={initials(u.name || u.email)} online={u.devices.some((d) => d.online)} />
              <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                <span className="cx-strong">{u.name || u.email}</span>
                <span className="muted cx-small">
                  {u.email} · {u.devices.length} {u.devices.length === 1 ? 'device' : 'devices'}
                </span>
              </span>
            </span>
            <span className="row" style={{ gap: 8 }}>
              <select aria-label={`What ${u.name || u.email} does`} value={job[u.email] ?? 'engineer'} onChange={(e) => setJob({ ...job, [u.email]: e.currentTarget.value as Job })}>
                <option value="engineer">Engineer</option>
                <option value="technician">Technician (hourly)</option>
                <option value="intake">Intake</option>
              </select>
              <button className="primary" disabled={add.pending} onClick={() => add.run(u.email, u.name || u.email.split('@')[0] || u.email, job[u.email] ?? 'engineer')}>
                Add to Kronos
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
